import * as React from 'react';
import axios from '../utils/axios';

/* ============================================================================
   Estado do sistema visto pela aba aberta.

   Uma chamada por minuto a `GET /geral/estado` responde tres perguntas que a tela
   nao tinha como responder:

     1. O SERVIDOR CAIU?      a chamada falha sem resposta (rede/timeout/5xx).
     2. SUBIU VERSAO NOVA?    `versao` diferente da que estava no ar quando a aba
                              carregou - o codigo desta aba e o antigo.
     3. A API REINICIOU?      `iniciadoEm` mudou: processo novo. Importa porque
                              tarefa em andamento pode ter morrido no meio.

   E a MESMA chamada e o sinal de vida que alimenta a lista de quem esta online
   (o backend identifica quem chamou pelo token). Um poll, quatro coisas.

   ---------------------------------------------------------------------------
   DECISOES QUE MERECEM REGISTRO

   PRIMEIRA LEITURA E A REFERENCIA, nao a versao compilada no bundle. A tentacao e
   comparar a resposta com o `package.json` embutido no front, mas front e API sao
   versionados juntos e sobem em momentos diferentes: por alguns segundos, todo
   mundo veria "sistema atualizado" sem nada ter mudado. Guardando o que a API
   respondeu na PRIMEIRA vez, o aviso so aparece quando o valor muda de fato -
   ou seja, quando alguem subiu algo depois de a aba abrir.

   QUEDA E SO O QUE NAO TEM RESPOSTA. Rede fora, timeout, DNS - ou 5xx, que e o
   servidor dizendo que quebrou. Qualquer 4xx significa que alguem recebeu e
   respondeu, entao o servidor esta de pe. Aprendido na pratica: com a API rodando
   uma versao anterior a esta rota, o 404 estava sendo contado como falha e a tela
   anunciava "sem conexao" com tudo funcionando.

   401 NAO E QUEDA. Token expirado devolve 401 e o interceptor do axios ja trata
   (limpa a sessao e manda para o login). Se eu contasse 401 como falha, a tela
   piscaria "servidor fora do ar" no caminho para o login.

   PRECISA DE DUAS FALHAS SEGUIDAS para acusar queda. Uma requisicao perdida
   acontece em rede corporativa a toda hora; avisar na primeira faria a faixa
   aparecer e desaparecer sozinha, que e o jeito mais rapido de ensinar alguem a
   ignorar avisos.

   ABA ESCONDIDA NAO PINGA. `document.hidden` corta o laco: navegador em segundo
   plano ja estrangula temporizador, e vinte abas abertas de madrugada nao precisam
   conversar com a API. Ao voltar para a aba, consulta na hora - que e justamente
   quando a pessoa quer saber se ainda esta tudo de pe.
   ============================================================================ */

const INTERVALO_MS = 60_000;

export type EstadoSistema = {
  /** `true` depois de duas falhas seguidas. */
  fora: boolean;
  /** Versao nova disponivel - a aba esta com codigo antigo. */
  atualizado: boolean;
  /** O processo da API reiniciou desde que a aba abriu. */
  reiniciado: boolean;
  /** Versao que a API respondeu na primeira leitura. */
  versao: string | null;
};

type Resposta = { versao?: string; iniciadoEm?: string; agora?: string };

export default function useEstadoSistema(ativo = true): EstadoSistema {
  const [estado, setEstado] = React.useState<EstadoSistema>({
    fora: false,
    atualizado: false,
    reiniciado: false,
    versao: null,
  });

  // Referencia da primeira leitura. Em ref, e nao em estado: mudar isso nao deve
  // provocar renderizacao, e o efeito nao pode se reiniciar por causa dela.
  const referencia = React.useRef<{ versao?: string; iniciadoEm?: string } | null>(null);
  const falhas = React.useRef(0);
  const rotaAusente = React.useRef(false);

  React.useEffect(() => {
    if (!ativo) return undefined;

    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const consultar = async () => {
      if (!vivo) return;

      try {
        const { data } = await axios.get<Resposta>('/geral/estado');
        if (!vivo) return;

        falhas.current = 0;

        if (!referencia.current) {
          referencia.current = { versao: data?.versao, iniciadoEm: data?.iniciadoEm };
          setEstado((e) => ({ ...e, fora: false, versao: data?.versao ?? null }));
        } else {
          const ref = referencia.current;
          setEstado((e) => ({
            ...e,
            fora: false,
            atualizado: Boolean(data?.versao && ref.versao && data.versao !== ref.versao),
            reiniciado: Boolean(data?.iniciadoEm && ref.iniciadoEm && data.iniciadoEm !== ref.iniciadoEm),
          }));
        }
      } catch (erro: any) {
        if (!vivo) return;

        const status = erro?.response?.status;

        // 401/403 sao permissao, nao queda - o interceptor do axios ja trata.
        if (status === 401 || status === 403) return;

        /* 🔴 404 TAMBEM NAO E QUEDA - e foi assim que a faixa apareceu errada.
           A API subiu com uma versao ANTERIOR a esta rota: ela respondeu 404 (na
           verdade o 404 do servidor de estaticos), o codigo contou como falha e a
           tela anunciou "sem conexao com o servidor" com o servidor no ar e todo o
           resto do sistema funcionando.

           A regra certa: so e queda quando NAO HA RESPOSTA (rede, timeout, DNS) ou
           quando o servidor responde 5xx. Qualquer 4xx significa que alguem do
           outro lado recebeu e respondeu.

           Com 404 o laco PARA: a rota nao existe nesta versao da API, e insistir
           uma vez por minuto para sempre e gasto sem resposta possivel. Volta a
           funcionar sozinho no proximo carregamento da pagina, depois que a API for
           atualizada. */
        if (status === 404) {
          rotaAusente.current = true;
          return;
        }

        const semResposta = !erro?.response;
        const erroDeServidor = typeof status === 'number' && status >= 500;
        if (!semResposta && !erroDeServidor) return;

        falhas.current += 1;
        if (falhas.current >= 2) setEstado((e) => ({ ...e, fora: true }));
      } finally {
        // Nao reagenda se a rota nao existe nesta versao da API.
        if (vivo && !rotaAusente.current) timer = setTimeout(consultar, INTERVALO_MS);
      }
    };

    const aoVoltarParaAba = () => {
      if (!document.hidden) {
        clearTimeout(timer);
        consultar();
      }
    };

    consultar();
    document.addEventListener('visibilitychange', aoVoltarParaAba);

    return () => {
      vivo = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', aoVoltarParaAba);
    };
  }, [ativo]);

  return estado;
}
